import { useState, useCallback, useEffect, useMemo } from 'react';
import { BehaviorSubject } from 'rxjs';

/**
 * Wraps a {@link BehaviorSubject} into a React hook.
 * @param subject The subject to hookify.
 */
const useBehaviorSubject = subject => {
  const [value, setValue] = useState(subject.value);
  const emitValue = useCallback(value => {
    subject.next(value);
  }, [subject]);
  useEffect(() => {
    const subscription = subject.subscribe(setValue);
    return () => {
      subscription.unsubscribe();
    };
  }, [subject]);
  return [value, emitValue];
};

/**
 * Factory function to create a value definition specific useValue hook.
 * @param schema A schema to validate the values being set.
 */
function createUseValue(schema) {
  const subject = new BehaviorSubject(undefined);
  const validate = value => typeof value === 'undefined' ? undefined : schema.parse(value);

  /**
   * The actual useValue hook which expects a reference to a {@link BehaviorSubject}.
   * @param subject       The BehaviourSubject holding the current value.
   * @param initialValue  A initial value that should be applied when the hook runs for the first time
   *                      and the current value is undefined.
   */
  const useValue = subject => {
    const [value, setValue] = useBehaviorSubject(subject);
    const validateAndSetValue = useCallback(value => {
      setValue(validate(value));
    }, [setValue]);
    return useMemo(() => {
      return [value, validateAndSetValue];
    }, [value, validateAndSetValue]);
  };
  return () => useValue(subject);
}

function createValue(valueDefinition) {
  const {
    schema,
    id
  } = valueDefinition;
  if (typeof id.description === 'undefined') {
    throw new TypeError('Symbol set as value id does not contain a description.');
  }
  return Object.freeze({
    useValue: createUseValue(schema)
  });
}

function createNamespace(namespace) {
  if (typeof namespace.id.description === 'undefined') {
    throw new TypeError('Symbol set as namespace id does not contain a description.');
  }
  return Object.freeze(namespace);
}

export { createNamespace, createValue };
//# sourceMappingURL=index.js.map
